/**
 * Generic scroll reveal via data-reveal attributes.
 * Usage: <h2 data-reveal="fade-up" data-reveal-delay="0.3">...</h2>
 *
 * Init runs as soon as DOM + GSAP are ready (not window.load).
 * Shared debounced ScrollTrigger.refresh is exposed as
 * window.__mt5bwScheduleSTRefresh for other MT5BW scripts.
 */
(function () {
  "use strict";

  const SELECTOR = "[data-reveal]";
  const READY_ATTR = "data-reveal-ready";
  const HERO_SELECTOR = ".hero-wrap";
  const LOAD_REFRESH_FLAG = "__mt5bwScrollRevealLoadRefresh";
  const LAYOUT_OBSERVER_FLAG = "__mt5bwScrollRevealLayoutObserver";
  const INIT_FLAG = "__mt5bwScrollRevealInited";
  const SHARED_REFRESH_KEY = "__mt5bwScheduleSTRefresh";
  const REFRESH_DEBOUNCE_MS = 300;

  function isMobile() {
    return window.matchMedia("(max-width: 720px)").matches;
  }

  function getScrollStart() {
    return isMobile() ? "top 65%" : "top 70%";
  }

  function getHeroScrollStart() {
    return getScrollStart();
  }

  function getDefaultY() {
    return isMobile() ? 20 : 35;
  }

  function getDefaultDuration() {
    return isMobile() ? 0.45 : 0.6;
  }

  function markReady(el) {
    el.setAttribute(READY_ATTR, "");
    el.removeAttribute("reveal-on-scroll");
  }

  function createSharedRefreshScheduler() {
    let timer = 0;
    let lastScrollHeight = 0;

    return function scheduleSTRefresh(force) {
      if (typeof ScrollTrigger === "undefined") {
        return;
      }

      if (timer) {
        clearTimeout(timer);
      }

      timer = window.setTimeout(() => {
        timer = 0;
        if (ScrollTrigger.isRefreshing) {
          return;
        }

        const nextHeight = document.documentElement.scrollHeight;
        if (!force && lastScrollHeight && Math.abs(nextHeight - lastScrollHeight) < 2) {
          return;
        }

        lastScrollHeight = nextHeight;
        ScrollTrigger.refresh();
      }, REFRESH_DEBOUNCE_MS);
    };
  }

  if (typeof window[SHARED_REFRESH_KEY] !== "function") {
    window[SHARED_REFRESH_KEY] = createSharedRefreshScheduler();
  }

  function refreshTriggers(force) {
    window[SHARED_REFRESH_KEY](force);
  }

  function animateElement(el, options) {
    const {
      trigger,
      start,
      delay = 0,
      y = getDefaultY(),
      duration = getDefaultDuration(),
    } = options;

    gsap.set(el, { y, opacity: 0, force3D: true });

    gsap.to(el, {
      y: 0,
      opacity: 1,
      duration,
      ease: "power2.out",
      delay,
      force3D: true,
      scrollTrigger: {
        trigger,
        start,
        toggleActions: "play none none none",
        once: true,
      },
    });
  }

  function initHeroReveal(hero, reduceMotion) {
    const elements = Array.from(hero.querySelectorAll(SELECTOR)).filter(
      (el) => !el.hasAttribute(READY_ATTR)
    );
    if (!elements.length) {
      return new Set();
    }

    elements.forEach(markReady);

    if (reduceMotion) {
      gsap.set(elements, { y: 0, opacity: 1, clearProps: "transform" });
      return new Set(elements);
    }

    const start = getHeroScrollStart();
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start,
        toggleActions: "play none none none",
        once: true,
      },
    });

    elements.forEach((el) => {
      const delay = parseFloat(el.getAttribute("data-reveal-delay") || "0", 10);
      const y = parseFloat(el.getAttribute("data-reveal-y") || String(getDefaultY()), 10);
      const duration = parseFloat(
        el.getAttribute("data-reveal-duration") || String(getDefaultDuration()),
        10
      );

      gsap.set(el, { y, opacity: 0, force3D: true });
      tl.to(
        el,
        {
          y: 0,
          opacity: 1,
          duration,
          ease: "power2.out",
          force3D: true,
        },
        delay
      );
    });

    return new Set(elements);
  }

  function observeLayoutChanges() {
    if (window[LAYOUT_OBSERVER_FLAG]) {
      return;
    }

    const onAssetLoad = (event) => {
      if (event.target instanceof HTMLImageElement || event.target instanceof HTMLVideoElement) {
        refreshTriggers(false);
      }
    };

    document.addEventListener("load", onAssetLoad, true);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && document.body) {
      resizeObserver = new ResizeObserver(() => refreshTriggers(false));
      resizeObserver.observe(document.body);
    }

    const stopAssetLoadListening = () => {
      document.removeEventListener("load", onAssetLoad, true);
    };

    // After full load, image batching is done — ResizeObserver is enough.
    if (document.readyState === "complete") {
      stopAssetLoadListening();
    } else {
      window.addEventListener("load", stopAssetLoadListening, { once: true });
    }

    window[LAYOUT_OBSERVER_FLAG] = {
      onAssetLoad,
      resizeObserver,
      stopAssetLoadListening,
    };
  }

  function init() {
    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    const allElements = document.querySelectorAll(SELECTOR);
    if (!allElements.length) {
      return;
    }

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const defaultStart = getScrollStart();
    const handled = new Set();

    document.querySelectorAll(HERO_SELECTOR).forEach((hero) => {
      initHeroReveal(hero, reduceMotion).forEach((el) => handled.add(el));
    });

    if (reduceMotion) {
      const remaining = Array.from(allElements).filter(
        (el) => !handled.has(el) && !el.hasAttribute(READY_ATTR)
      );
      remaining.forEach(markReady);
      gsap.set(remaining, { y: 0, opacity: 1, clearProps: "transform" });
      return;
    }

    allElements.forEach((el) => {
      if (handled.has(el) || el.hasAttribute(READY_ATTR)) {
        return;
      }

      markReady(el);

      const delay = parseFloat(el.getAttribute("data-reveal-delay") || "0", 10);
      const y = parseFloat(el.getAttribute("data-reveal-y") || String(getDefaultY()), 10);
      const start = el.getAttribute("data-reveal-start") || defaultStart;
      const duration = parseFloat(
        el.getAttribute("data-reveal-duration") || String(getDefaultDuration()),
        10
      );

      animateElement(el, {
        trigger: el,
        start,
        delay,
        y,
        duration,
      });
    });

    observeLayoutChanges();
    refreshTriggers(true);

    // After full load, correct trigger positions once images settle.
    if (!window[LOAD_REFRESH_FLAG]) {
      window[LOAD_REFRESH_FLAG] = true;
      if (document.readyState === "complete") {
        refreshTriggers(true);
      } else {
        window.addEventListener("load", () => refreshTriggers(true), { once: true });
      }
    }
  }

  function bootstrap() {
    if (window[INIT_FLAG]) {
      return;
    }
    window[INIT_FLAG] = true;

    const restoredScrollY = window.scrollY;

    // Mid-page refresh: reset scroll briefly so ScrollTrigger measures correctly.
    if (restoredScrollY > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, 0);
          init();
          requestAnimationFrame(() => {
            refreshTriggers(true);
            requestAnimationFrame(() => window.scrollTo(0, restoredScrollY));
          });
        });
      });
      return;
    }

    init();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap, { once: true });
  } else {
    bootstrap();
  }

  document.addEventListener("shopify:section:load", () => {
    window[INIT_FLAG] = false;
    bootstrap();
    refreshTriggers(true);
  });

  window.addEventListener(
    "pagehide",
    () => {
      const observerState = window[LAYOUT_OBSERVER_FLAG];
      if (!observerState) {
        return;
      }

      observerState.stopAssetLoadListening?.();
      document.removeEventListener("load", observerState.onAssetLoad, true);
      observerState.resizeObserver?.disconnect();
      delete window[LAYOUT_OBSERVER_FLAG];
    },
    { once: true }
  );
})();
